import { useState, useRef, useEffect } from "react";
import { PatternFormat } from "react-number-format";
import { handleKeyPress } from "../../../../../utils/inputValidation";
import useAuthStore from "../../../../../stores/auth";
import { fetchDominioData } from "../../../../../fetchDominioData";

import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import CloudDoneIcon from "@mui/icons-material/CloudDone";

import useHrmEmployeeStore from "../../../../../stores/hrEmployee";
import {
  MunicipioOption,
  resolveMunicipioEnderecoCode,
} from "../../../../../utils/syncAddressMunicipio";

const tipo_endereco = [
  { label: "Aeroporto", value: 1 },
  { label: "Distrito", value: 10 },
  { label: "Esplanada", value: 11 },
  { label: "Estação", value: 12 },
  { label: "Estrada", value: 13 },
  { label: "Favela", value: 14 },
  { label: "Fazenda", value: 15 },
  { label: "Feira", value: 16 },
  { label: "Jardim", value: 17 },
  { label: "Ladeira", value: 18 },
  { label: "Lago", value: 19 },
  { label: "Alameda", value: 2 },
  { label: "Lagoa", value: 20 },
  { label: "Largo", value: 21 },
  { label: "Loteamento", value: 22 },
  { label: "Morro", value: 23 },
  { label: "Núcleo", value: 24 },
  { label: "Parque", value: 25 },
  { label: "Passarela", value: 26 },
  { label: "Pátio", value: 27 },
  { label: "Praça", value: 28 },
  { label: "Quadra", value: 29 },
  { label: "Área", value: 3 },
  { label: "Recanto", value: 30 },
  { label: "Residencial", value: 31 },
  { label: "Rodovia", value: 32 },
  { label: "Rua", value: 33 },
  { label: "Setor", value: 34 },
  { label: "Sítio", value: 35 },
  { label: "Travessa", value: 36 },
  { label: "Trecho", value: 37 },
  { label: "Trevo", value: 38 },
  { label: "Avenida", value: 4 },
  { label: "Vereda", value: 40 },
  { label: "Via", value: 41 },
  { label: "Viaduto", value: 42 },
  { label: "Viela", value: 43 },
  { label: "Vila", value: 44 },
  { label: "Vale", value: 49 },
  { label: "Campo", value: 5 },
  { label: "Chácara", value: 6 },
  { label: "Colônia", value: 7 },
  { label: "Condomínio", value: 8 },
  { label: "Conjunto", value: 9 },
  { label: "Outro", value: 99 },
];

const HRManagementEmployeeAddress = () => {
  const [state, setState] = useState(false);
  const [municipios, setMunicipios] = useState<
    { codigo_municipio: string; nome_municipio: string; codigo_uf: string }[]
  >([]);
  const [municipiosLoading, setMunicipiosLoading] = useState(false);
  const [ufLading, setUfLoading] = useState(false);
  const [paises, setPaises] = useState<
    { codigo_pais: string; nome_pais: string }[]
  >([]);
  const [paisesLoading, setPaisesLoading] = useState(false);
  const [ufs, setUfs] = useState<
    { codigo_uf: string; sigla_uf: string; nome_uf: string }[]
  >([]);
  const [ufsLoading, setUfsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<{
    codigo_municipio: string;
    nome_municipio: string;
    codigo_uf: string;
  } | null>(null);
  const openRef = useRef(null);
  const saveDraft = useHrmEmployeeStore((state: any) => state.saveDraft);
  const changeDraft = useHrmEmployeeStore((state: any) => state.changeDraft);
  const isSaving = useHrmEmployeeStore((state: any) => state.isSaving);
  const data = useHrmEmployeeStore((state: any) => state.data);
  const addDrawersRef = useHrmEmployeeStore(
    (state: any) => state.addDrawersRef,
  );
  const company = useAuthStore((state) => state.company);

  const getMunicipiosData = async () => {
    setMunicipiosLoading(true);
    try {
      const response = await fetchDominioData(
        `/empresa/${company.reference}/municipios`,
        {
          method: "GET",
        },
      );
      if (response) {
        setMunicipios(response.rows);
      }
    } catch (error) {
      console.error(error);
    }
    setMunicipiosLoading(false);
  };

  const getPaisesData = async () => {
    setPaisesLoading(true);
    try {
      const response = await fetchDominioData(
        `/empresa/${company.reference}/paises`,
        {
          method: "GET",
        },
      );
      if (response) {
        setPaises(response.rows);
      }
    } catch (error) {
      console.error(error);
    }
    setPaisesLoading(false);
  };

  const getUfsData = async () => {
    setUfsLoading(true);
    try {
      const response = await fetchDominioData(
        `/empresa/${company.reference}/uf`,
        {
          method: "GET",
        },
      );
      if (response) {
        setUfs(response.rows);
      }
    } catch (error) {
      console.error(error);
    }
    setUfsLoading(false);
  };

  useEffect(() => addDrawersRef({ name: "address", ref: openRef }), []);

  useEffect(() => {
    if (state) {
      getMunicipiosData();
      getPaisesData();
      getUfsData();
    }
  }, [state]);

  useEffect(() => {
    if (!state || !municipios.length || data.draft?.municipio_endereco) {
      return;
    }

    const draft = data.draft as Record<string, unknown>;
    const municipioCode = resolveMunicipioEnderecoCode(
      draft,
      municipios as MunicipioOption[],
    );

    if (municipioCode === null) {
      return;
    }

    changeDraft({
      target: {
        name: "municipio_endereco",
        value: municipioCode,
      },
    });
  }, [state, municipios, data.draft]);

  return (
    <>
      <Button
        fullWidth
        ref={openRef}
        variant="outlined"
        onClick={() => {
          setState(true);
        }}
        disabled={data?.status === "editing" ? false : true}
      >
        <Stack spacing={1} alignItems="center">
          <PersonPinCircleIcon fontSize="large" />
          <div>Endereço</div>
        </Stack>
      </Button>
      <Drawer
        anchor="left"
        open={state}
        onClose={() => {
          setState(false);
        }}
      >
        <Box sx={{ p: 3, pt: 2, width: 400 }} width={300} height="100%">
          <Stack height="100%" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ flex: 1 }}
                alignItems="center"
              >
                <Typography variant="body1">Endereço</Typography>
                {data.status === "editing" && (
                  <Chip
                    color="success"
                    variant="outlined"
                    icon={
                      isSaving === true ? (
                        <CircularProgress color="warning" size={18} />
                      ) : (
                        <CloudDoneIcon fontSize="small" />
                      )
                    }
                    label={isSaving ? "Salvando..." : "Tudo Salvo"}
                  />
                )}
              </Stack>
              <IconButton
                onClick={() => {
                  setState(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <TextField
              name="tipo_endereco"
              fullWidth
              variant="filled"
              select
              size="small"
              label="Tipo de Endereço"
              defaultValue={data.draft?.tipo_endereco || null}
              SelectProps={{ native: true }}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              sx={{ mb: 1 }}
              required
            >
              <option value=""></option>
              {tipo_endereco.map((item, index) => (
                <option value={item.value} key={`tipo_endereco_${index}`}>
                  {item.label}
                </option>
              ))}
            </TextField>
            <PatternFormat
              format="#####-###"
              name="cep"
              autoComplete="password"
              fullWidth
              variant="filled"
              label="CEP"
              defaultValue={data.draft?.cep || null}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              customInput={TextField}
              sx={{ mb: 1 }}
              required
            />
            <TextField
              name="endereco"
              fullWidth
              variant="filled"
              label="Endereço"
              defaultValue={data.draft?.endereco || null}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              sx={{ mb: 1 }}
              required
              onKeyPress={handleKeyPress}
            />
            <TextField
              name="endereco_numero"
              fullWidth
              variant="filled"
              label="Número"
              defaultValue={data.draft?.endereco_numero || null}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              sx={{ mb: 1 }}
              required
              onKeyPress={handleKeyPress}
            />
            <TextField
              name="endereco_complemento"
              fullWidth
              variant="filled"
              label="Complemento"
              defaultValue={data.draft?.endereco_complemento || null}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              sx={{ mb: 1 }}
              onKeyPress={handleKeyPress}
            />
            <TextField
              name="endereco_bairro"
              fullWidth
              variant="filled"
              label="Bairro"
              defaultValue={data.draft?.endereco_bairro || null}
              onBlur={() => saveDraft()}
              onChange={changeDraft}
              sx={{ mb: 1 }}
              required
              onKeyPress={handleKeyPress}
            />
            <Autocomplete
              options={municipios}
              getOptionLabel={(option) => option.nome_municipio}
              loading={municipiosLoading}
              value={
                municipios.find(
                  (m) => m.nome_municipio === data.draft?.endereco_cidade,
                ) || null
              }
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter((option) =>
                  option.nome_municipio.toLowerCase().includes(searchTerm),
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.codigo_municipio === value.codigo_municipio
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={`${option.codigo_municipio}-${option.nome_municipio}`}
                >
                  {option.nome_municipio}
                </li>
              )}
              onChange={(event, newValue) => {
                if (newValue) {
                  changeDraft({
                    target: {
                      name: "endereco_cidade",
                      value: newValue.nome_municipio,
                    },
                  });

                  changeDraft({
                    target: {
                      name: "municipio_endereco",
                      value: newValue.codigo_municipio,
                    },
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="endereco_cidade"
                  label="Cidade"
                  variant="filled"
                  required
                  sx={{ mb: 1 }}
                  onBlur={() => saveDraft()}
                  onChange={changeDraft}
                />
              )}
            />
            <Autocomplete
              options={ufs}
              getOptionLabel={(option) => option.sigla_uf}
              loading={ufsLoading}
              value={
                ufs.find((u) => u.sigla_uf === data.draft?.endereco_estado) ||
                null
              }
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.sigla_uf.toLowerCase().includes(searchTerm) ||
                    option.nome_uf.toLowerCase().includes(searchTerm),
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.codigo_uf === value.codigo_uf
              }
              renderOption={(props, option) => (
                <li {...props} key={`${option.codigo_uf}-${option.sigla_uf}`}>
                  {option.sigla_uf} - {option.nome_uf}
                </li>
              )}
              onChange={(event, newValue) => {
                if (newValue) {
                  changeDraft({
                    target: {
                      name: "endereco_estado",
                      value: newValue.sigla_uf,
                    },
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="endereco_estado"
                  label="Estado (UF)"
                  variant="filled"
                  required
                  sx={{ mb: 1 }}
                  onBlur={() => saveDraft()}
                  onChange={changeDraft}
                />
              )}
            />
            <Autocomplete
              options={paises}
              getOptionLabel={(option) => option.nome_pais}
              loading={paisesLoading}
              value={
                paises.find(
                  (p) =>
                    String(p.codigo_pais) ===
                    String(data.draft?.codigo_pais_endereco),
                ) || null
              }
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter((option) =>
                  option.nome_pais.toLowerCase().includes(searchTerm),
                );
              }}
              isOptionEqualToValue={(option, value) =>
                option.codigo_pais === value.codigo_pais
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={`${option.codigo_pais}-${option.nome_pais}`}
                >
                  {option.nome_pais}
                </li>
              )}
              onChange={(event, newValue) => {
                changeDraft({
                  target: {
                    name: "codigo_pais_endereco",
                    value: newValue?.codigo_pais ?? "",
                  },
                });
                saveDraft();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="codigo_pais_endereco"
                  label="País de endereço"
                  variant="filled"
                  required
                  sx={{ mb: 1 }}
                  onBlur={() => saveDraft()}
                  onChange={changeDraft}
                />
              )}
            />
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default HRManagementEmployeeAddress;
